import { useEffect, useState, type KeyboardEvent } from 'react'
import { NavLink } from 'react-router-dom'
import { Pencil, Check, X } from 'lucide-react'
import { PageHeader, Panel, Button, FormRow, Textarea, Select, Banner, useToast } from '../../ds/index'
import { useApi } from '../../lib/useApi'
import { put } from '../../lib/api'
import { dateShort } from '../../lib/format'
import { Icon } from '../../components/Icon'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'

type Question = {
  key: string; label: string; type: 'text' | 'textarea' | 'url' | 'number' | 'select' | 'multi' | 'tags' | 'miles' | 'money' | 'choice' | 'list' | 'email' | 'phone'
  required?: boolean; help?: string; placeholder?: string; options?: string[]; max?: number; min?: number; anywhere?: string; legacy?: string[]
  monthly?: { label?: string; help?: string }; onlyMonthly?: boolean; zero?: string
}
type Section = { key: string; title: string; blurb: string; questions: Question[] }
type Answer = string | number | string[]
type Intake = {
  sections: Section[]; answers: Record<string, Answer>; updated_at: string | null; category: string | null; areas: string[]
  completion: { percent: number; complete: boolean; done: number; required: number; sections: { key: string; required: number; done: number; answered: number; complete: boolean }[] }
}

/** Business profile and Leads and account: the two halves of a client's own settings. */
export function ProfileTabs() {
  return (
    <nav className="page-tabs" aria-label="Your account">
      <NavLink to="/portal/profile" className={({ isActive }) => (isActive ? 'page-tab is-on' : 'page-tab')}>Business profile</NavLink>
      <NavLink to="/portal/settings" className={({ isActive }) => (isActive ? 'page-tab is-on' : 'page-tab')}>Leads and account</NavLink>
    </nav>
  )
}

/** A monthly-service business: its amounts read as monthly and it can have a startup fee. */
const isMonthly = (a: Record<string, Answer | ''>) => a.job_billing === 'Monthly service'
const labelOf = (q: Question, monthly: boolean) => (monthly && q.monthly?.label) || q.label

const show = (v: Answer | undefined) => (v === undefined ? '' : Array.isArray(v) ? v.join(', ') : String(v))

/**
 * The client's business profile: four short sections of questions. Every
 * answer is visible as it stands and editable at any time, one section at a
 * time. The AI reads all of it when it decides which leads suit this client.
 */
export default function ClientProfile({ onboarding }: { onboarding?: { onContinue: () => void } } = {}) {
  const { data, error } = useApi<Intake>('/api/client/intake')
  const [intake, setIntake] = useState<Intake | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  useEffect(() => { if (data) setIntake(data) }, [data])

  // A new client lands straight in the first section they have not finished.
  useEffect(() => {
    if (!data || editing) return
    if (data.completion.done === 0 && !data.updated_at) setEditing(data.sections[0].key)
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <ErrorBlock error={error} />
  if (!intake) return <LoadingBlock />
  const c = intake.completion
  const next = intake.sections.find((s) => !c.sections.find((x) => x.key === s.key)?.complete)

  return (
    <>
      {onboarding ? (
        <>
          <div className="ob-head">
            <span className="hero-kicker">Step 1 of 2</span>
            <h1>Tell us about your business</h1>
            <p className="muted">Thirteen quick questions in four sections. You can change any answer later from your portal.</p>
          </div>
          <Banner tone="warn" title="Fill this in carefully">Our AI reads these answers to decide which leads go to you. The more specific you are about what you do, where you work and the jobs you want, the better the leads you get.</Banner>
        </>
      ) : (
        <>
          <PageHeader title="Your profile" subtitle="Tell us about your business. Leads are matched to you on these answers, so the clearer they are, the better your leads." />
          <ProfileTabs />
        </>
      )}

      <div className="profile-progress">
        <div className="profile-progress-text">
          <span className="hero-kicker">{c.complete ? 'Profile complete' : 'Profile'}</span>
          <strong>{c.percent}%</strong>
          <span className="muted">
            {c.complete ? `You can change any answer at any time.${intake.updated_at ? ` Last updated ${dateShort(intake.updated_at)}.` : ''}`
              : `${next ? `Next: ${next.title}. ` : ''}Every section helps us send you the right leads.`}
          </span>
        </div>
        <ol className="profile-steps" aria-label="Sections">
          {intake.sections.map((s) => {
            const st = c.sections.find((x) => x.key === s.key)!
            return (
              <li key={s.key}>
                <a href={`#sec-${s.key}`} className={st.complete ? 'is-done' : ''} onClick={() => setEditing(st.complete ? editing : s.key)}>
                  <span className="profile-step-dot" aria-hidden="true">{st.complete ? <Icon of={Check} size={12} /> : null}</span>
                  {s.title}
                </a>
              </li>
            )
          })}
        </ol>
        <div className="meter"><span style={{ width: `${c.percent}%` }} /></div>
      </div>

      <div className="stack">
        {intake.sections.map((s) => (
          <SectionCard key={s.key} section={s} intake={intake} editing={editing === s.key}
                       onEdit={() => setEditing(s.key)} onCancel={() => setEditing(null)}
                       onSaved={(next) => {
                         setIntake(next)
                         // Straight on to the next unfinished section, until the profile is done.
                         const nxt = next.sections.find((x) => !next.completion.sections.find((y) => y.key === x.key)?.complete)
                         const go = nxt && !next.completion.complete ? nxt.key : null
                         setEditing(go)
                         if (go) setTimeout(() => document.getElementById(`sec-${go}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60)
                       }} />
        ))}
      </div>
      {onboarding && (
        <div className="ob-continue">
          <span className="muted">{c.complete ? 'All set. Next, your monthly lead credit.' : `${c.required - c.done} required answer${c.required - c.done === 1 ? '' : 's'} to go.`}</span>
          <Button onClick={onboarding.onContinue} disabled={!c.complete}>Continue</Button>
        </div>
      )}
      {(intake.category || intake.areas.length > 0) && (
        <p className="muted" style={{ fontSize: 13, marginTop: 'var(--s-6)' }}>
          Handpicked has you set up for {[intake.category, intake.areas.length ? `in ${intake.areas.join(', ')}` : ''].filter(Boolean).join(' ')}. To change that, contact them.
        </p>
      )}
    </>
  )
}

function SectionCard({ section, intake, editing, onEdit, onCancel, onSaved }: {
  section: Section; intake: Intake; editing: boolean; onEdit: () => void; onCancel: () => void; onSaved: (i: Intake) => void
}) {
  const st = intake.completion.sections.find((x) => x.key === section.key)!
  const missing = section.questions.filter((q) => q.required && intake.answers[q.key] === undefined)
  return (
    <section id={`sec-${section.key}`} className={`profile-section${editing ? ' is-editing' : ''}`} aria-labelledby={`h-${section.key}`}>
      <Panel>
        <div className="panel-header">
          <div>
            <h2 className="panel-title" id={`h-${section.key}`}>{section.title}</h2>
            <p className="muted profile-blurb">{section.blurb}</p>
          </div>
          {!editing && (
            <span className="row-tight" style={{ alignItems: 'center' }}>
              {st.complete ? <span className="pill is-good">Done</span> : <span className="pill is-warn">{missing.length} to answer</span>}
              <Button variant="secondary" onClick={onEdit}><Icon of={Pencil} size={14} /> {st.answered ? 'Edit' : 'Answer'}</Button>
            </span>
          )}
        </div>
        {editing ? <SectionForm section={section} answers={intake.answers} onCancel={onCancel} onSaved={onSaved} /> : (
          st.answered === 0 ? <p className="muted" style={{ margin: 0 }}>Not answered yet.</p> : (
            <dl className="profile-answers">
              {section.questions.filter((q) => !q.onlyMonthly || isMonthly(intake.answers)).map((q) => (
                <div key={q.key} className={q.type === 'textarea' ? 'is-wide' : ''}>
                  <dt>{labelOf(q, isMonthly(intake.answers))}</dt>
                  <dd>{intake.answers[q.key] === undefined ? (q.type === 'choice' ? q.options![0] : <span className="muted">{q.required ? 'Needed' : 'Not answered'}</span>)
                    : Array.isArray(intake.answers[q.key]) ? <span className="profile-chips">{(intake.answers[q.key] as string[]).map((v) => <span key={v} className="chip">{v}</span>)}</span>
                    : q.type === 'money' && typeof intake.answers[q.key] === 'number' ? `$${Number(intake.answers[q.key]).toLocaleString('en-US')}`
                    : show(intake.answers[q.key])}</dd>
                </div>
              ))}
            </dl>
          )
        )}
      </Panel>
    </section>
  )
}

function SectionForm({ section, answers, onCancel, onSaved }: { section: Section; answers: Record<string, Answer>; onCancel: () => void; onSaved: (i: Intake) => void }) {
  const toast = useToast()
  const [f, setF] = useState<Record<string, Answer | ''>>(() => Object.fromEntries(section.questions.map((q) => [q.key, answers[q.key] ?? (q.type === 'multi' || q.type === 'tags' || q.type === 'list' ? [] : q.type === 'choice' ? q.options![0] : '')])))
  const monthly = isMonthly(f)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const set = (k: string, v: Answer | '') => setF((x) => ({ ...x, [k]: v }))

  const save = async () => {
    setErr(null)
    const blank = section.questions.filter((q) => q.required && (f[q.key] === '' || (Array.isArray(f[q.key]) && (f[q.key] as string[]).length === 0)))
    // Required answers can be saved later; say what is still needed, but save what is there.
    const body = Object.fromEntries(section.questions.map((q) => {
      const v = f[q.key]
      if (q.onlyMonthly && !monthly) return [q.key, null]
      if (q.type === 'number') return [q.key, v === '' ? null : Number(v)]
      if (Array.isArray(v)) return [q.key, q.type === 'list' ? v.map((x) => String(x).trim()).filter(Boolean) : v]
      return [q.key, v === '' ? null : v]
    }))
    setBusy(true)
    try {
      const next = await put<Intake>(`/api/client/intake/${section.key}`, { answers: body })
      toast(blank.length ? `Saved. Still needed: ${blank.map((q) => q.label).join(', ')}` : `${section.title} saved`, blank.length ? 'neutral' : 'pos')
      onSaved(next)
    } catch (e) { setErr((e as Error).message) } finally { setBusy(false) }
  }

  return (
    <div className="profile-form">
      <div className="form-grid">
        {section.questions.filter((q) => !q.onlyMonthly || monthly).map((q) => (
          <div key={q.key} className={q.type === 'textarea' || q.type === 'multi' || q.type === 'tags' || q.type === 'list' || q.type === 'miles' || q.type === 'money' || q.type === 'choice' ? 'is-wide' : ''}>
            <FormRow label={`${labelOf(q, monthly)}${q.required || q.type === 'choice' ? '' : ' (optional)'}`} htmlFor={`q-${q.key}`} help={(monthly && q.monthly?.help) || q.help}>
              <QuestionInput q={q} value={f[q.key]} onChange={(v) => set(q.key, v)} />
            </FormRow>
          </div>
        ))}
      </div>
      {err && <p className="form-error" role="alert">{err}</p>}
      <div className="row-tight profile-form-actions">
        <Button onClick={save} disabled={busy}>{busy ? 'Saving...' : 'Save'}</Button>
        <Button variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
      </div>
    </div>
  )
}

function QuestionInput({ q, value, onChange }: { q: Question; value: Answer | ''; onChange: (v: Answer | '') => void }) {
  const id = `q-${q.key}`
  switch (q.type) {
    case 'textarea':
      return <Textarea id={id} rows={3} maxLength={q.max} value={String(value)} onChange={(e) => onChange(e.target.value)} />
    case 'select':
      return (
        <Select id={id} value={String(value)} onChange={(e) => onChange(e.target.value)}>
          <option value="">Choose one</option>
          {q.options!.map((o) => <option key={o} value={o}>{o}</option>)}
        </Select>
      )
    case 'multi': {
      const list = Array.isArray(value) ? value : []
      return (
        <div className="choice-chips" role="group" aria-label={q.label} id={id}>
          {q.options!.map((o) => {
            const on = list.includes(o)
            return (
              <button key={o} type="button" className="filter-chip" aria-pressed={on}
                      onClick={() => onChange(on ? list.filter((x) => x !== o) : [...list, o])}>{on && <Icon of={Check} size={13} />} {o}</button>
            )
          })}
        </div>
      )
    }
    case 'choice':
      return (
        <div className="seg-choice" role="radiogroup" aria-label={q.label} id={id}>
          {q.options!.map((o) => (
            <button key={o} type="button" role="radio" aria-checked={value === o} className="seg-option" onClick={() => onChange(o)}>
              {value === o && <Icon of={Check} size={14} />} {o}
            </button>
          ))}
        </div>
      )
    case 'money':
      return <MoneyInput id={id} q={q} value={value} onChange={onChange} />
    case 'miles':
      return <MilesInput id={id} q={q} value={String(value)} onChange={onChange} />
    case 'tags':
      return <TagInput id={id} value={Array.isArray(value) ? value : []} onChange={onChange} max={q.max || 30} />
    case 'list': {
      // One line per item, kept in order; up to the maximum, blank lines dropped on save.
      const items = Array.isArray(value) ? value : []
      const rows = Math.max(q.min || 1, Math.min(q.max || 5, items.length + 1))
      const at = (i: number) => items[i] ?? ''
      return (
        <div className="list-input" id={id}>
          {Array.from({ length: rows }, (_, i) => (
            <input key={i} className="field" aria-label={`${q.label}, ${i + 1}`} placeholder={`${i + 1}.`} value={at(i)}
                   onChange={(e) => { const next = Array.from({ length: rows }, (_, j) => (j === i ? e.target.value : at(j))); onChange(next.filter((x, j) => x !== '' || j < next.length - 1)) }} />
          ))}
        </div>
      )
    }
    default:
      return (
        <input id={id} className="field" type={q.type === 'number' ? 'number' : q.type === 'url' ? 'url' : q.type === 'email' ? 'email' : q.type === 'phone' ? 'tel' : 'text'}
               inputMode={q.type === 'number' ? 'numeric' : q.type === 'phone' ? 'tel' : q.type === 'email' ? 'email' : undefined} autoComplete={q.type === 'email' ? 'email' : q.type === 'phone' ? 'tel' : undefined}
               placeholder={q.placeholder} maxLength={q.max} min={q.min} value={String(value)} onChange={(e) => onChange(e.target.value)} />
      )
  }
}

/**
 * How far they travel: a slider for any distance up to the max, a box to type
 * an exact number, or anywhere in the metro. Saved as words ("Up to 25 miles").
 */
function MilesInput({ id, q, value, onChange }: { id: string; q: Question; value: string; onChange: (v: string) => void }) {
  const min = q.min ?? 1
  const max = q.max ?? 100
  const anywhere = Boolean(q.anywhere) && value === q.anywhere
  const n = Number(/(\d+)/.exec(value)?.[1] || 0)
  const set = (raw: number) => {
    const m = Math.min(max, Math.max(min, Math.round(raw) || min))
    onChange(`Up to ${m} mile${m === 1 ? '' : 's'}`)
  }
  const shown = n || 20
  return (
    <div className="miles-input">
      <div className={`miles-row${anywhere ? ' is-off' : ''}${!n && !anywhere ? ' is-unset' : ''}`}>
        <input id={id} type="range" min={min} max={max} step={1} value={shown} disabled={anywhere}
               aria-label={`${q.label}, miles`} aria-valuetext={n ? `${n} miles` : 'not set'}
               style={{ ['--pct' as string]: n ? `${((shown - min) / (max - min)) * 100}%` : '0%' }}
               onChange={(e) => set(Number(e.target.value))} />
        <span className="miles-box">
          <input className="field" inputMode="numeric" aria-label={`${q.label}, exact miles`} value={n ? String(n) : ''} placeholder="20" disabled={anywhere}
                 onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); if (v === '') onChange(''); else set(Number(v)) }} />
          <span className="muted">miles</span>
        </span>
      </div>
      <div className="miles-foot">
        <span className={n || anywhere ? '' : 'muted'}>{anywhere ? 'No distance limit' : n ? `Up to ${n} mile${n === 1 ? '' : 's'}` : 'Slide or type a distance'}</span>
        {q.anywhere && (
          <button type="button" className="filter-chip" aria-pressed={anywhere} onClick={() => (anywhere ? set(shown) : onChange(q.anywhere!))}>
            {anywhere && <Icon of={Check} size={13} />} {q.anywhere}
          </button>
        )}
      </div>
    </div>
  )
}

/** Dollar stops for the slider: small jobs move in small steps, big ones in big steps. */
const MONEY_STOPS = [0, 100, 250, 500, 750, 1000, 1500, 2000, 2500, 3500, 5000, 7500, 10000, 15000, 20000, 25000, 35000, 50000, 75000, 100000, 150000, 250000]
const dollars = (n: number) => `$${n.toLocaleString('en-US')}`

/**
 * A dollar amount: a slider along MONEY_STOPS, or a box for any exact amount.
 * An answer from the old range list shows as written until it is changed.
 */
function MoneyInput({ id, q, value, onChange }: { id: string; q: Question; value: Answer | ''; onChange: (v: Answer | '') => void }) {
  const n = typeof value === 'number' ? value : value !== '' && /^\d+$/.test(String(value)) ? Number(value) : null
  const legacy = typeof value === 'string' && value !== '' && n === null ? value : null
  const nearest = n === null ? 6 : MONEY_STOPS.reduce((best, x, i) => (Math.abs(x - n) < Math.abs(MONEY_STOPS[best] - n) ? i : best), 0)
  const last = MONEY_STOPS.length - 1
  return (
    <div className="miles-input">
      <div className={`miles-row${n === null ? ' is-unset' : ''}`}>
        <input id={id} type="range" min={0} max={last} step={1} value={nearest}
               aria-label={`${q.label}, dollars`} aria-valuetext={n === null ? 'not set' : dollars(n)}
               style={{ ['--pct' as string]: n === null ? '0%' : `${(nearest / last) * 100}%` }}
               onChange={(e) => onChange(MONEY_STOPS[Number(e.target.value)])} />
        <span className="miles-box money-box">
          <span className="muted">$</span>
          <input className="field" inputMode="numeric" aria-label={`${q.label}, exact dollars`} placeholder={q.placeholder || 'Any'}
                 value={n === null ? '' : n.toLocaleString('en-US')}
                 onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); onChange(v === '' ? '' : Math.min(q.max ?? 10000000, Number(v))) }} />
        </span>
      </div>
      <div className="miles-foot">
        <span className={n === null ? 'muted' : ''}>{legacy ? `${legacy} (from before; slide or type to change)` : n === null ? 'Slide or type an amount' : n === 0 ? (q.zero || 'Any size') : `${dollars(n)}${nearest === last && n >= MONEY_STOPS[last] ? ' or more' : ''}`}</span>
        {n !== null && <button type="button" className="link-button" onClick={() => onChange('')}>Clear</button>}
      </div>
    </div>
  )
}

/** Places typed one at a time: Enter or a comma adds one, the x removes it. */
function TagInput({ id, value, onChange, max }: { id: string; value: string[]; onChange: (v: string[]) => void; max: number }) {
  const [text, setText] = useState('')
  const add = () => {
    const parts = text.split(',').map((t) => t.trim()).filter(Boolean)
    const next = [...value]
    for (const p of parts) if (!next.some((v) => v.toLowerCase() === p.toLowerCase()) && next.length < max) next.push(p)
    onChange(next)
    setText('')
  }
  const key = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add() }
    if (e.key === 'Backspace' && !text && value.length) onChange(value.slice(0, -1))
  }
  return (
    <div className="tag-input">
      {value.map((v) => (
        <span key={v} className="tag">{v}<button type="button" aria-label={`Remove ${v}`} onClick={() => onChange(value.filter((x) => x !== v))}><Icon of={X} size={12} /></button></span>
      ))}
      <input id={id} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={key} onBlur={add} placeholder={value.length ? 'Add another' : 'Type a place, press Enter'} />
    </div>
  )
}
